/**
 * sqlite3mt worker-bridge message contracts and handlers
 *
 * §96 §10.3–§10.4 (fault service), §16.3–§16.4 (checkpoint bridge),
 * R42 (runtimeEpoch stamping), R9/R75 (fatal wakeups).
 *
 * This module owns:
 * 1. Typed message contracts for fault requests, checkpoint launch, and
 *    checkpoint completion — all runtimeEpoch-stamped per R42.
 * 2. handleFaultRequest() — the Control Worker's §10.4 onFault handler that
 *    resolves a page fault against the live DB-view.
 * 3. launchCheckpointIfNeeded() — the §16.3 checkpoint launch guard.
 * 4. handleCheckpointDone() — the §16.3 onCheckpointDone handler.
 * 5. handleCheckpointWorkerRun() — the §16.4 Checkpoint Worker stub.
 */

import type {
    CheckpointMode,
    PageHash,
    Pgno,
    Root,
    WalSnapshot,
} from './types'

// ── §10.3 Fault request message ──────────────────────────────────

/**
 * Slot fault request payload from §10.3.
 *
 * The visible WAL frontier is carried so the Control Worker can service the
 * fault against the same DB-view/WAL-view the reader linearized against.
 */
export interface FaultRequest {
    pgno: Pgno
    slot: number
    gen: bigint
    visibleFrameMax: number
    visibleWalSalt0: number
    visibleWalSalt1: number
}

export interface FaultRequestMessage extends FaultRequest {
    type: 'fault'
    runtimeEpoch: bigint
}

// ── §16.3–§16.4 Checkpoint messages ──────────────────────────────

export interface RunCheckpointMessage {
    type: 'run-checkpoint'
    runtimeEpoch: bigint
    seq: number
    mode: CheckpointMode
}

export interface CheckpointDoneMessage {
    type: 'checkpoint-done'
    runtimeEpoch: bigint
    seq: number
    mode: CheckpointMode
    rc: number
    logFrames: number
    ckptFrames: number
}

// ── Message builders ──────────────────────────────────────────────

export function buildFaultRequestMessage(
    runtimeEpoch: bigint,
    request: FaultRequest
): FaultRequestMessage {
    return {
        type: 'fault',
        runtimeEpoch,
        ...request,
    }
}

export function faultRequestMatchesRuntimeEpoch(
    runtimeEpoch: bigint,
    msg: Pick<FaultRequestMessage, 'runtimeEpoch'>
): boolean {
    return msg.runtimeEpoch === runtimeEpoch
}

export function buildRunCheckpointMessage(
    runtimeEpoch: bigint,
    seq: number,
    mode: CheckpointMode
): RunCheckpointMessage {
    return {
        type: 'run-checkpoint',
        runtimeEpoch,
        seq,
        mode,
    }
}

export function buildCheckpointDoneMessage(
    runtimeEpoch: bigint,
    seq: number,
    mode: CheckpointMode,
    rc: number,
    logFrames: number,
    ckptFrames: number
): CheckpointDoneMessage {
    return {
        type: 'checkpoint-done',
        runtimeEpoch,
        seq,
        mode,
        rc,
        logFrames,
        ckptFrames,
    }
}

export function checkpointDoneMatchesInflight(
    runtimeEpoch: bigint,
    checkpointInflightSeq: number,
    msg: Pick<CheckpointDoneMessage, 'runtimeEpoch' | 'seq'>
): boolean {
    return msg.runtimeEpoch === runtimeEpoch
        && msg.seq === checkpointInflightSeq
}

// ── §10.4 Fault handler ──────────────────────────────────────────

/**
 * Fault handler state accessible to the Control Worker.
 *
 * Contains the mutable DB-view fields and WAL integration state
 * that the fault handler reads under the control-plane's single-writer
 * discipline.
 */
export interface FaultHandlerState {
    syncFatalError: unknown | null
    currentWalSalt0: number | null
    currentWalSalt1: number | null
    integratedFrameMax: number
    dbPageCount: number
    dbOverlay: ReadonlyMap<Pgno, PageHash>
    dbBaseRoot: Root
    stagedCas: Map<PageHash, Uint8Array>
    faultReadAhead?: FaultReadAheadState
}

/**
 * Bounded, demand-following read-ahead state for sqlite3mt page faults.
 *
 * This is not open-time hydration or full DB prefetch. The control worker only
 * uses it after it has observed sequential SQLite demand, and the prefetched
 * page blobs are cached under content-addressed hashes for later normal fault
 * validation.
 */
export interface FaultReadAheadState {
    maxPages: number
    initialWindowPages: number
    currentWindowPages: number
    sequentialTrigger: number
    lastDemandPgno: Pgno | null
    sequentialDemandCount: number
    prefetchedThroughPgno: Pgno | null
}

export function createFaultReadAheadState(maxPages: number, sequentialTrigger = 2): FaultReadAheadState | undefined {
    const normalizedMaxPages = Number.isFinite(maxPages) ? Math.trunc(maxPages) : 0
    const normalizedTrigger = Number.isFinite(sequentialTrigger) ? Math.trunc(sequentialTrigger) : 2
    const boundedMaxPages = Math.max(0, Math.min(64, normalizedMaxPages))
    if (boundedMaxPages <= 0) return undefined
    const initialWindowPages = Math.min(4, boundedMaxPages)
    return {
        maxPages: boundedMaxPages,
        initialWindowPages,
        currentWindowPages: initialWindowPages,
        sequentialTrigger: Math.max(2, Math.min(8, normalizedTrigger)),
        lastDemandPgno: null,
        sequentialDemandCount: 0,
        prefetchedThroughPgno: null,
    }
}

/**
 * Hooks that the fault handler calls for side effects and I/O.
 *
 * Injected so that the handler can be tested without SAB or real blob stores.
 */
export interface FaultHandlerHooks {
    /** Publish fault result into the slot via §10.4 publishFaultState. */
    publishFault(
        slot: number,
        gen: bigint,
        pgno: Pgno,
        newState: number,
        data: Uint8Array | null
    ): void

    /** Check whether the reader needs to wait for the frontier to advance. */
    readerNeedsFrontierWait(
        readerSalt0: number,
        readerSalt1: number,
        readerFrameMax: number,
        frontierSnapshot: { valid: boolean; salt0: number; salt1: number; integratedFrameMax: number }
    ): boolean

    /** Advance DB head (absorb pending deltas into dbOverlay). */
    advanceDbHead(): WalSnapshot

    /** Get DB-view page hash at pgno. */
    getDbPageHash(pgno: Pgno): Promise<PageHash | null>

    /** Get blob from durable local store. Returns null if not found. */
    getBlobFromDurable(hash: PageHash): Promise<Uint8Array | null>

    /** Best-effort durable cache fill for remote fault/read-ahead results. */
    putBlobToDurable?(hash: PageHash, data: Uint8Array): Promise<void> | void

    /** Get blob from remote RPC. Returns null if not found. */
    getBlobFromRpc(hash: PageHash): Promise<Uint8Array | null>

    /** Get multiple blobs from remote RPC. Returns only found hashes. */
    getBlobsFromRpc?(hashes: PageHash[]): Promise<Map<PageHash, Uint8Array>>

    /** Abort if fatal. Throws syncFatalError if set. */
    abortIfFatal(): void
}

function publishFaultRetryOrError(
    state: FaultHandlerState,
    hooks: FaultHandlerHooks,
    req: FaultRequest,
): void {
    hooks.publishFault(
        req.slot,
        req.gen,
        req.pgno,
        state.syncFatalError != null ? FAULT_SLOT_ERROR : FAULT_SLOT_RETRY,
        null,
    )
}

function observeDemandForReadAhead(
    readAhead: FaultReadAheadState | undefined,
    pgno: Pgno
): number {
    if (readAhead == null) return 0

    const previous = readAhead.lastDemandPgno
    const sequential = previous != null && pgno === previous + 1

    readAhead.sequentialDemandCount = sequential
        ? readAhead.sequentialDemandCount + 1
        : 1
    readAhead.lastDemandPgno = pgno

    if (!sequential) {
        readAhead.currentWindowPages = readAhead.initialWindowPages
        return 0
    }
    if (readAhead.sequentialDemandCount < readAhead.sequentialTrigger) {
        return 0
    }

    const windowPages = readAhead.currentWindowPages
    readAhead.currentWindowPages = Math.min(
        readAhead.maxPages,
        Math.max(readAhead.initialWindowPages, readAhead.currentWindowPages * 2),
    )
    return windowPages
}

function cacheRemoteBlobsBestEffort(
    hooks: FaultHandlerHooks,
    blobs: ReadonlyMap<PageHash, Uint8Array>
): void {
    if (hooks.putBlobToDurable == null) return
    for (const [hash, data] of blobs) {
        void Promise.resolve(hooks.putBlobToDurable(hash, data)).catch(() => {})
    }
}

/** Slot states — mirrors SlotState but without importing the const enum. */
export const FAULT_SLOT_READY = 2
export const FAULT_SLOT_ERROR = 3
export const FAULT_SLOT_RETRY = 4

/**
 * §10.4 onFault handler — Control Worker services a page fault.
 *
 * This function implements the full pseudocode from §10.4:
 * 1. Reject if runtime is fatal (publish ERROR).
 * 2. Advance DB head to absorb pending WAL deltas.
 * 3. If reader's frontier is ahead of the integrated frontier, RETRY.
 * 4. If pgno > dbPageCount after advance, RETRY (EOF may have changed).
 * 5. Resolve page hash from DB-view overlay + base root.
 * 6. Fetch page data from stagedCas → durable → RPC (3-tier lookup).
 * 7. Re-validate frontier and page hash after await (detect races).
 * 8. Publish READY with data, or RETRY/ERROR on mismatch.
 */
export async function handleFaultRequest(
    state: FaultHandlerState,
    hooks: FaultHandlerHooks,
    req: FaultRequest
): Promise<void> {
    // R9: Fatal gate — reject immediately
    if (state.syncFatalError != null) {
        hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_ERROR, null)
        return
    }

    // Absorb pending WAL deltas into DB-view before looking up page hash
    hooks.advanceDbHead()

    // Snapshot current frontier state after advance
    const fs0 = {
        valid: state.currentWalSalt0 != null,
        salt0: state.currentWalSalt0 ?? 0,
        salt1: state.currentWalSalt1 ?? 0,
        integratedFrameMax: state.integratedFrameMax,
    }

    // If the reader saw a frontier that is ahead of what we've integrated,
    // the reader's view is stale — ask for retry
    if (hooks.readerNeedsFrontierWait(
        req.visibleWalSalt0, req.visibleWalSalt1, req.visibleFrameMax, fs0
    )) {
        hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_RETRY, null)
        return
    }

    // After advance, re-check page count
    if (req.pgno > state.dbPageCount) {
        hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_RETRY, null)
        return
    }

    try {
        const pageHash0 = await hooks.getDbPageHash(req.pgno)
        hooks.abortIfFatal()

        if (pageHash0 == null) {
            // Page not found in DB-view — if page count changed, retry
            if (req.pgno > state.dbPageCount) {
                hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_RETRY, null)
            } else {
                hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_ERROR, null)
            }
            return
        }

        // 3-tier blob lookup: stagedCas → durable → RPC
        let data: Uint8Array | null
        try {
            data =
                state.stagedCas.get(pageHash0)
                ?? await hooks.getBlobFromDurable(pageHash0)
                ?? await hooks.getBlobFromRpc(pageHash0)
        } catch {
            publishFaultRetryOrError(state, hooks, req)
            return
        }
        hooks.abortIfFatal()

        // Re-validate frontier after async awaits
        const fs1 = {
            valid: state.currentWalSalt0 != null,
            salt0: state.currentWalSalt0 ?? 0,
            salt1: state.currentWalSalt1 ?? 0,
            integratedFrameMax: state.integratedFrameMax,
        }

        if (hooks.readerNeedsFrontierWait(
            req.visibleWalSalt0, req.visibleWalSalt1, req.visibleFrameMax, fs1
        )) {
            hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_RETRY, null)
            return
        }

        // Double-check page hash hasn't changed during await
        const pageHash1 = await hooks.getDbPageHash(req.pgno)
        hooks.abortIfFatal()

        if (pageHash1 !== pageHash0) {
            hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_RETRY, null)
            return
        }

        if (data == null) {
            hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_ERROR, null)
            return
        }

        hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_READY, data)
    } catch {
        // Hash/frontier failures are structural for this view unless the
        // runtime has already gone fatal. Remote data-plane failures are
        // handled above as RETRY so transient object fetch errors do not poison
        // the slot into a permanent SQLITE_IOERR.
        hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_ERROR, null)
    }
}

/**
 * Batched §10.4 fault handling.
 *
 * This preserves demand-driven reads: current faults are pages SQLite already
 * asked for, and optional read-ahead is only a small bounded continuation after
 * observed sequential demand. The Control Worker still performs the same
 * validation and re-validation as handleFaultRequest(), but durable misses
 * that reach the remote tier are collected into one RPC batch when there is
 * actually more than one remote object to fetch.
 */
export async function handleFaultRequests(
    state: FaultHandlerState,
    hooks: FaultHandlerHooks,
    requests: FaultRequest[]
): Promise<void> {
    if (requests.length === 0) return

    type Candidate = {
        req: FaultRequest
        pageHash0: PageHash
        data: Uint8Array | null
        needsRemote: boolean
    }
    const candidates: Candidate[] = []
    const currentRemoteHashes = new Set<PageHash>()
    const readAheadRemoteHashes = new Set<PageHash>()

    for (const req of requests) {
        if (state.syncFatalError != null) {
            hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_ERROR, null)
            continue
        }

        hooks.advanceDbHead()

        const fs0 = {
            valid: state.currentWalSalt0 != null,
            salt0: state.currentWalSalt0 ?? 0,
            salt1: state.currentWalSalt1 ?? 0,
            integratedFrameMax: state.integratedFrameMax,
        }

        if (hooks.readerNeedsFrontierWait(
            req.visibleWalSalt0, req.visibleWalSalt1, req.visibleFrameMax, fs0
        )) {
            hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_RETRY, null)
            continue
        }

        if (req.pgno > state.dbPageCount) {
            hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_RETRY, null)
            continue
        }

        try {
            const pageHash0 = await hooks.getDbPageHash(req.pgno)
            hooks.abortIfFatal()

            if (pageHash0 == null) {
                hooks.publishFault(
                    req.slot,
                    req.gen,
                    req.pgno,
                    req.pgno > state.dbPageCount ? FAULT_SLOT_RETRY : FAULT_SLOT_ERROR,
                    null,
                )
                continue
            }

            let readAheadWindowPages = observeDemandForReadAhead(state.faultReadAhead, req.pgno)

            const stagedData = state.stagedCas.get(pageHash0) ?? null
            if (stagedData != null) {
                candidates.push({ req, pageHash0, data: stagedData, needsRemote: false })
            } else {
                const durableData = await hooks.getBlobFromDurable(pageHash0)
                hooks.abortIfFatal()
                candidates.push({
                    req,
                    pageHash0,
                    data: durableData,
                    needsRemote: durableData == null,
                })
                if (durableData == null) {
                    currentRemoteHashes.add(pageHash0)
                    readAheadWindowPages = Math.max(
                        readAheadWindowPages,
                        state.faultReadAhead?.initialWindowPages ?? 0,
                    )
                }
            }

            if (
                readAheadWindowPages > 0
                && state.faultReadAhead != null
                && (
                    state.faultReadAhead.prefetchedThroughPgno == null
                    || req.pgno >= state.faultReadAhead.prefetchedThroughPgno
                )
            ) {
                const startPgno = (req.pgno + 1) as Pgno
                const maxPgno = Math.min(state.dbPageCount, req.pgno + readAheadWindowPages)
                let prefetchedThrough = state.faultReadAhead.prefetchedThroughPgno ?? req.pgno

                for (let pgno = startPgno; pgno <= maxPgno; pgno = (pgno + 1) as Pgno) {
                    const hash = await hooks.getDbPageHash(pgno)
                    hooks.abortIfFatal()
                    if (hash == null || state.stagedCas.has(hash) || currentRemoteHashes.has(hash)) {
                        prefetchedThrough = pgno
                        continue
                    }
                    const durableData = await hooks.getBlobFromDurable(hash)
                    hooks.abortIfFatal()
                    if (durableData == null) {
                        readAheadRemoteHashes.add(hash)
                    }
                    prefetchedThrough = pgno
                }

                state.faultReadAhead.prefetchedThroughPgno = prefetchedThrough
            }
        } catch {
            hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_ERROR, null)
        }
    }

    const remoteHashes = Array.from(new Set([
        ...currentRemoteHashes,
        ...readAheadRemoteHashes,
    ]))
    let remoteBlobs = new Map<PageHash, Uint8Array>()
    let remoteError: unknown = null
    if (remoteHashes.length > 0) {
        try {
            if (hooks.getBlobsFromRpc == null) {
                const fetched = new Map<PageHash, Uint8Array>()
                for (const hash of remoteHashes) {
                    const bytes = await hooks.getBlobFromRpc(hash)
                    if (bytes != null) {
                        fetched.set(hash, bytes)
                    }
                }
                remoteBlobs = fetched
            } else {
                remoteBlobs = await hooks.getBlobsFromRpc(remoteHashes)
            }
            cacheRemoteBlobsBestEffort(hooks, remoteBlobs)
            hooks.abortIfFatal()
        } catch (error) {
            remoteError = error
        }
    }

    for (const candidate of candidates) {
        const { req, pageHash0 } = candidate
        const data = candidate.needsRemote
            ? remoteBlobs.get(pageHash0) ?? null
            : candidate.data

        try {
            if (candidate.needsRemote && remoteError != null) {
                publishFaultRetryOrError(state, hooks, req)
                continue
            }

            hooks.abortIfFatal()

            const fs1 = {
                valid: state.currentWalSalt0 != null,
                salt0: state.currentWalSalt0 ?? 0,
                salt1: state.currentWalSalt1 ?? 0,
                integratedFrameMax: state.integratedFrameMax,
            }

            if (hooks.readerNeedsFrontierWait(
                req.visibleWalSalt0, req.visibleWalSalt1, req.visibleFrameMax, fs1
            )) {
                hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_RETRY, null)
                continue
            }

            const pageHash1 = await hooks.getDbPageHash(req.pgno)
            hooks.abortIfFatal()

            if (pageHash1 !== pageHash0) {
                hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_RETRY, null)
                continue
            }

            if (data == null) {
                hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_ERROR, null)
                continue
            }

            hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_READY, data)
        } catch {
            hooks.publishFault(req.slot, req.gen, req.pgno, FAULT_SLOT_ERROR, null)
        }
    }
}

// ── §16.3 Checkpoint launch guard ─────────────────────────────────

/**
 * State needed by launchCheckpointIfNeeded().
 *
 * Mirrors the Control Worker's checkpoint-related volatile state from §5.
 */
export interface CheckpointLaunchState {
    syncFatalError: unknown | null
    checkpointQuiescentBusy: boolean
    checkpointBusy: boolean
    checkpointWanted: boolean
    checkpointWantedMode: CheckpointMode | null
    externalClientRequestCount?: number
    pushBusy?: boolean
    mergeBusy: boolean
    reconcileIntent: unknown | null
    pendingBaseAdvance: unknown | null
    headAdvancePreparing: boolean
    checkpointInflightSeq: number
}

/**
 * Hooks for checkpoint launch side effects.
 */
export interface CheckpointLaunchHooks {
    /** Begin the quiescent window — throws on failure or fatal. */
    beginCheckpointQuiescentWindow(): Promise<WalSnapshot>

    /** True when any connection currently owns SQLite WAL_WRITE. */
    hasWalWriteHolders?(): boolean

    /** True when any connection currently holds a main-db shared lock. */
    hasMainDbReaders?(): boolean

    /** Send run-checkpoint message to Checkpoint Worker. */
    postRunCheckpoint(msg: RunCheckpointMessage): void
}

/** Sentinel for "no checkpoint mode wanted". */
export const CHECKPOINT_MODE_NONE: CheckpointMode | null = null

/**
 * Resolve the effective checkpoint mode, upgrading if the no-progress
 * threshold is met.
 */
export function maxCheckpointMode(
    current: CheckpointMode | null,
    candidate: CheckpointMode
): CheckpointMode {
    if (current == null) {
        return candidate
    }
    const order: ReadonlyArray<CheckpointMode> = ['PASSIVE', 'RESTART', 'TRUNCATE']
    const ci = order.indexOf(current)
    const candi = order.indexOf(candidate)
    return ci >= candi ? current : candidate
}

/**
 * §16.3 launchCheckpointIfNeeded — decides whether to start a checkpoint.
 *
 * Returns true if a checkpoint was launched, false otherwise.
 */
export async function launchCheckpointIfNeeded(
    runtimeEpoch: bigint,
    state: CheckpointLaunchState,
    hooks: CheckpointLaunchHooks,
    maxPassiveNoProgress: number,
    checkpointNoProgressCount: number
): Promise<boolean> {
    // Guard chain from §16.3
    if (state.syncFatalError != null) return false
    if (state.checkpointQuiescentBusy) return false
    if (state.checkpointBusy) return false
    if (!state.checkpointWanted) return false
    if ((state.externalClientRequestCount ?? 0) > 0) return false
    if (hooks.hasWalWriteHolders?.() === true) return false
    if (state.pushBusy === true) return false
    if (state.mergeBusy) return false
    if (state.reconcileIntent != null) return false
    if (state.pendingBaseAdvance != null) return false
    if (state.headAdvancePreparing) return false

    state.checkpointBusy = true
    state.checkpointWanted = false

    try {
        await hooks.beginCheckpointQuiescentWindow()
    } catch {
        state.checkpointBusy = false
        if (state.syncFatalError == null) {
            state.checkpointWanted = true
        }
        return false
    }

    const wantedMode = state.checkpointWantedMode ?? 'PASSIVE'
    const mode = wantedMode === 'TRUNCATE' && hooks.hasMainDbReaders?.() === true
        ? 'PASSIVE'
        : wantedMode
    state.checkpointWantedMode = CHECKPOINT_MODE_NONE
    const seq = state.checkpointInflightSeq + 1
    state.checkpointInflightSeq = seq

    hooks.postRunCheckpoint(
        buildRunCheckpointMessage(runtimeEpoch, seq, mode)
    )
    return true
}

// ── §16.3 Checkpoint done handler ─────────────────────────────────

/** SQLITE_OK return code from sqlite3_wal_checkpoint_v2. */
export const SQLITE_OK = 0

/**
 * State needed by handleCheckpointDone().
 */
export interface CheckpointDoneState {
    syncFatalError: unknown | null
    checkpointBusy: boolean
    checkpointInflightSeq: number
    checkpointWanted: boolean
    checkpointWantedMode: CheckpointMode | null
    checkpointNoProgressCount: number
}

/**
 * Hooks for checkpoint completion side effects.
 */
export interface CheckpointDoneHooks {
    /** Advance DB head (absorb pending deltas). */
    advanceDbHead(): WalSnapshot

    /** End the quiescent window — reopens checkpoint gate if not fatal. */
    endCheckpointQuiescentWindow(): void

    /** Try to launch another checkpoint if needed. */
    launchCheckpointIfNeeded(): Promise<boolean>

    /** Trigger compaction check. */
    maybeCompactDbBaseRoot(): void

    /** Trigger sync engine restart kick (§14.1). */
    maybeStartPushOrImport(): void
}

/**
 * §16.3 onCheckpointDone — handles a checkpoint-done message from
 * the Checkpoint Worker.
 *
 * Returns true if the message was accepted (epoch+seq match),
 * false if it was ignored (stale epoch or seq).
 */
export async function handleCheckpointDone(
    runtimeEpoch: bigint,
    state: CheckpointDoneState,
    hooks: CheckpointDoneHooks,
    msg: CheckpointDoneMessage,
    maxPassiveNoProgress: number
): Promise<boolean> {
    // R42: Ignore stale-generation messages
    if (msg.runtimeEpoch !== runtimeEpoch) return false
    if (msg.seq !== state.checkpointInflightSeq) return false

    let postCheckpointKick = false

    try {
        state.checkpointBusy = false
        hooks.advanceDbHead()

        if (state.syncFatalError == null) {
            if (msg.rc === SQLITE_OK) {
                if (msg.logFrames >= 0 && msg.ckptFrames >= 0 && msg.ckptFrames < msg.logFrames) {
                    state.checkpointNoProgressCount += 1
                } else {
                    state.checkpointNoProgressCount = 0
                }
            } else {
                state.checkpointNoProgressCount += 1
            }

            const snap = hooks.advanceDbHead()
            const backlog = Math.max(0, snap.mxFrame - snap.nBackfill)

            if (backlog > 0) {
                state.checkpointWanted = true
                state.checkpointWantedMode = maxCheckpointMode(
                    state.checkpointWantedMode,
                    state.checkpointNoProgressCount >= maxPassiveNoProgress
                        ? 'RESTART'
                        : 'PASSIVE'
                )
            }

            postCheckpointKick = true
        }
    } finally {
        hooks.endCheckpointQuiescentWindow()
    }

    // R35 / R66: Post-checkpoint kick outside the quiescent window
    if (postCheckpointKick && state.syncFatalError == null) {
        if (state.checkpointWanted) {
            await hooks.launchCheckpointIfNeeded()
        }
        hooks.maybeCompactDbBaseRoot()
        hooks.maybeStartPushOrImport()
    }

    return true
}

// ── §16.4 Checkpoint Worker handler ───────────────────────────────

/**
 * Result of a single wal_checkpoint_v2 call.
 */
export interface CheckpointWorkerResult {
    rc: number
    logFrames: number
    ckptFrames: number
}

/**
 * Hooks for the Checkpoint Worker's run handler.
 */
export interface CheckpointWorkerHooks {
    /** Perform the actual sqlite3_wal_checkpoint_v2 call. */
    runWalCheckpoint(mode: CheckpointMode): CheckpointWorkerResult
}

/**
 * §16.4 Checkpoint Worker message handler.
 *
 * Receives a run-checkpoint message, performs the checkpoint,
 * and returns the completion message to send back.
 */
export function handleCheckpointWorkerRun(
    msg: RunCheckpointMessage,
    hooks: CheckpointWorkerHooks
): CheckpointDoneMessage {
    let rc = SQLITE_OK
    let logFrames = -1
    let ckptFrames = -1

    try {
        const result = hooks.runWalCheckpoint(msg.mode)
        rc = result.rc
        logFrames = result.logFrames
        ckptFrames = result.ckptFrames
    } catch {
        rc = 1 // generic SQLITE_ERROR
    }

    return buildCheckpointDoneMessage(msg.runtimeEpoch, msg.seq, msg.mode, rc, logFrames, ckptFrames)
}
